import HTTP from "@/_helpers/http";
import { AUTHENTICATION__API } from "@/_helpers/urls";
import toast from "@/services/Notification";

const state = {
  registerInfo: {
    fname: "",
    lname: "",
    company_name: "",
    country: "",
    owner_type: 1,
    email: "",
    inv_referred_by: "",
    multiply_by: 0,
    password: "",
    confirmPassword: "",
    otp: "",
    referred_by: null,
    otpSent: 0,
    recaptcha_token: "",
    recaptcha_session_token: "",
  },
  token: null,
  isOtpSent: false,
  isOtpVerified: false,
  isRegistered: false,
  registrationError: null,
};

const mutations = {
  REGISTER_ACCOUNT_TYPE(state, type) {
    if (type === "Individual") {
      state.registerInfo.owner_type = 1;
      state.registerInfo.otpSent = 0;
    } else if (type === "Company") {
      state.registerInfo.owner_type = 2;
      state.registerInfo.otpSent = 0;
    }
  },

  SET_REGISTER_INFO(state, { field, value }) {
    state.registerInfo[field] = value;
  },

  SET_OTP_SENT(state, value) {
    state.isOtpSent = value;
  },

  SET_OTP_SENT_STEP(state, value) {
    state.registerInfo.otpSent = value;
  },

  SET_OTP_VERIFIED(state, value) {
    state.isOtpVerified = value;
  },

  SET_REGISTERED(state, value) {
    state.isRegistered = value;
    state.registerInfo.otpSent = 1;
  },

  SET_REGISTRATION_ERROR(state, error) {
    state.registrationError = error;
  },

  SET_TOKEN(state, data) {
    state.token = data;
  },

  RESET(state) {
    state.registerInfo.fname = "";
    state.registerInfo.lname = "";
    state.registerInfo.company_name = "";
    state.registerInfo.country = "";
    state.registerInfo.email = "";
    state.registerInfo.inv_referred_by = "";
    state.registerInfo.multiply_by = 0;
    state.registerInfo.password = "";
    state.registerInfo.confirmPassword = "";
    state.registerInfo.otp = "";
    state.registerInfo.referred_by = null;
    state.registerInfo.otpSent = 0;
    state.registerInfo.recaptcha_token = "";
    state.registerInfo.recaptcha_session_token = "";

    state.token = null;
    state.isOtpSent = false;
    state.isOtpVerified = false;
    state.isRegistered = false;
    state.registrationError = null;
  },

  SET_REFERRED_BY(state, value) {
    state.registerInfo.referred_by = value;
  },
};

const actions = {
  initializeReferral({ commit }) {
    const urlParams = new URLSearchParams(window.location.search);
    const refId = urlParams.get("referred_by");
    if (refId) {
      commit("SET_REFERRED_BY", refId);
    }
  },

  async sendOTP({ state, commit }) {
    try {
      let urlParams = new URLSearchParams(window.location.search);
      let inv_referred_by = urlParams.get("inv_referred_by") || null;
      let referred_by = urlParams.get("referred_by") || null;

      const emailExists = await HTTP().post(AUTHENTICATION__API.EMAILCHECK, {
        email: state.registerInfo.email,
      });

      const response = await HTTP({
        trackProgress: true,
        loadingText: "Processing...",
      }).post(AUTHENTICATION__API.SENDREGISTEROTP, {
        multiply_by: state.registerInfo.multiply_by || null,
        name: state.registerInfo.fname + " " + state.registerInfo.lname,
        country: state.registerInfo.country,
        email: state.registerInfo.email,
        inv_referred_by: inv_referred_by,
        password: state.registerInfo.password,
        owner_type: state.registerInfo.owner_type,
        otp: state.registerInfo.otp,
        referred_by: referred_by,
        recaptcha_token: state.registerInfo.recaptcha_token, // added
      });

      commit("SET_OTP_SENT", true);
      commit("SET_TOKEN", response.data.token || null);

      // save backend-issued recaptcha session token for OTP + final register step
      commit("SET_REGISTER_INFO", {
        field: "recaptcha_session_token",
        value: response.data.recaptcha_session_token || "",
      });

      if (emailExists.data.exists) {
        toast.success("Investor Account Added Successfully");
        setTimeout(() => {
          window.location.href = "/";
        }, 500);
      } else {
        toast.success("Otp sent successfully");
        commit("SET_OTP_SENT_STEP", 1);
      }

      return response.data;
    } catch (error) {
      console.log(error?.response?.data?.message);
      commit("SET_OTP_SENT", false);
      commit("SET_OTP_SENT_STEP", 0);

      commit("SET_REGISTER_INFO", {
        field: "recaptcha_session_token",
        value: "",
      });

      toast.error(error?.response?.data?.message || "OTP sending failed");
      throw error;
    }
  },

  async previousStep({ commit }) {
    try {
      commit("SET_OTP_SENT_STEP", 0);
      commit("SET_OTP_VERIFIED", false);

      // user returned to first step, so clear backend captcha session
      commit("SET_REGISTER_INFO", {
        field: "recaptcha_session_token",
        value: "",
      });
    } catch (error) {
      console.log(error);
      commit("SET_OTP_SENT", false);
    }
  },

  async verifyOTP({ state, commit }) {
    try {
      const response = await HTTP({
        trackProgress: true,
        loadingText: "Processing...",
      }).post(AUTHENTICATION__API.AUTHACCOUNTVERIFY, {
        code: state.registerInfo.otp,
        token: state.token,
        recaptcha_session_token: state.registerInfo.recaptcha_session_token, // added
        email: state.registerInfo.email, // useful for backend validation if needed
      });

      if (response.status === 200) {
        commit("SET_OTP_VERIFIED", true);
        return response.data;
      }
    } catch (error) {
      commit("SET_OTP_VERIFIED", false);
      commit("SET_REGISTRATION_ERROR", "OTP verification failed");
      toast.error(error?.response?.data?.message || "OTP verification failed.");
      throw error;
    }
  },

  async register({ state, commit }) {
    try {
      let urlParams = new URLSearchParams(window.location.search);
      let referred_by = urlParams.get("referred_by") || null;
      let inv_referred_by = urlParams.get("inv_referred_by") || null;

      const res = await HTTP({
        trackProgress: true,
        loadingText: "Processing...",
      }).post(AUTHENTICATION__API.AUTHREGISTER, {
        fname: state.registerInfo.fname,
        lname: state.registerInfo.lname,
        country: state.registerInfo.country,
        email: state.registerInfo.email,
        inv_referred_by: inv_referred_by,
        multiply_by: state.registerInfo.multiply_by || null,
        password: state.registerInfo.password,
        owner_type: state.registerInfo.owner_type,
        otp: state.registerInfo.otp,
        referred_by: referred_by,
        recaptcha_session_token: state.registerInfo.recaptcha_session_token, // added
      });

      commit("SET_REGISTERED", true);

      if (res.status === 200) {
        toast.success("Registration success");
        commit("RESET");
      }

      return res.data;
    } catch (error) {
      console.log(error);
      commit("SET_REGISTRATION_ERROR", "Registration failed!");
      toast.error(error?.response?.data?.message || "Registration failed!");
      throw error;
    }
  },
};

const getters = {
  getRegisterInfo: (state) => state.registerInfo,
  isOtpSent: (state) => state.isOtpSent,
  isOtpVerified: (state) => state.isOtpVerified,
  isRegistered: (state) => state.isRegistered,
  registrationError: (state) => state.registrationError,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
